import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/AuthStyles.css';

const RegisterPage: React.FC = () => {
    const [formData, setFormData] = useState({
        prenom: '',
        nom: '',
        email: '',
        telephone: '',
        motDePasse: '',
        confirmMotDePasse: '',
        nomEntreprise: '',
        typeCompte: 'professionnel' as 'professionnel' | 'particulier'
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [registrationSuccess, setRegistrationSuccess] = useState(false);

    const { register } = useAuth();
    const navigate = useNavigate();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Validation
        if (formData.motDePasse !== formData.confirmMotDePasse) {
            setError('Les mots de passe ne correspondent pas');
            return;
        }

        if (formData.motDePasse.length < 6) {
            setError('Le mot de passe doit contenir au moins 6 caractères');
            return;
        }

        setLoading(true);

        try {
            const { confirmMotDePasse, ...formDataRaw } = formData;

            // Map French form fields to English API fields
            const registrationData = {
                email: formDataRaw.email,
                password: formDataRaw.motDePasse,
                first_name: formDataRaw.prenom,
                last_name: formDataRaw.nom,
                phone: formDataRaw.telephone,
                company_name: formDataRaw.nomEntreprise,
                account_type: formDataRaw.typeCompte === 'professionnel' ? 'professional' as const : 'individual' as const
            };

            await register(registrationData);
            // If we reach here, user was approved immediately (shouldn't happen with current setup)
            navigate('/');
        } catch (err: any) {
            // Check if this is an approval message (success case requiring approval)
            if (err.message?.includes('approbation')) {
                setRegistrationSuccess(true);
                setError('');
            } else {
                setError(err.message || 'Erreur lors de l\'inscription');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <div className="auth-container">
                <div className="auth-card">
                    <div className="auth-header">
                        <h1 className="auth-logo">Eldaï</h1>
                        <h2 className="auth-title">
                            Créer votre compte
                        </h2>
                        <p className="auth-subtitle">
                            Ou{' '}
                            <Link to="/login">
                                connectez-vous à votre compte existant
                            </Link>
                        </p>
                    </div>

                    <form className="auth-form" onSubmit={handleSubmit}>
                        {error && (
                            <div className="auth-error">
                                {error}
                            </div>
                        )}

                        {registrationSuccess && (
                            <div className="auth-success">
                                <div className="auth-success-header">
                                    Demande d'inscription envoyée !
                                </div>
                                <div className="auth-success-content">
                                    <p>
                                        Votre demande d'inscription a été envoyée avec succès.
                                        Un administrateur va examiner votre demande et vous recevrez
                                        une confirmation par email une fois votre compte approuvé.
                                    </p>
                                    <p>
                                        <Link to="/login">
                                            Retourner à la page de connexion
                                        </Link>
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="auth-fields">
                            {/* Type de compte */}
                            <div className="auth-field">
                                <label htmlFor="typeCompte" className="auth-label">
                                    Type de compte
                                </label>
                                <select
                                    id="typeCompte"
                                    name="typeCompte"
                                    value={formData.typeCompte}
                                    onChange={handleChange}
                                    className="auth-select"
                                >
                                    <option value="professionnel">Professionnel</option>
                                    <option value="particulier">Particulier</option>
                                </select>
                            </div>

                            {/* Prénom et Nom */}
                            <div className="auth-field-group">
                                <div className="auth-field">
                                    <label htmlFor="prenom" className="auth-label">
                                        Prénom
                                    </label>
                                    <input
                                        id="prenom"
                                        name="prenom"
                                        type="text"
                                        required
                                        value={formData.prenom}
                                        onChange={handleChange}
                                        className="auth-input"
                                        placeholder="Prénom"
                                    />
                                </div>
                                <div className="auth-field">
                                    <label htmlFor="nom" className="auth-label">
                                        Nom
                                    </label>
                                    <input
                                        id="nom"
                                        name="nom"
                                        type="text"
                                        required
                                        value={formData.nom}
                                        onChange={handleChange}
                                        className="auth-input"
                                        placeholder="Nom"
                                    />
                                </div>
                            </div>

                            {/* Email */}
                            <div className="auth-field">
                                <label htmlFor="email" className="auth-label">
                                    Email
                                </label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    required
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="auth-input"
                                    placeholder="votre@email.com"
                                />
                            </div>

                            {/* Téléphone */}
                            <div className="auth-field">
                                <label htmlFor="telephone" className="auth-label">
                                    Téléphone
                                </label>
                                <input
                                    id="telephone"
                                    name="telephone"
                                    type="tel"
                                    required
                                    value={formData.telephone}
                                    onChange={handleChange}
                                    className="auth-input"
                                    placeholder="01 23 45 67 89"
                                />
                            </div>

                            {/* Nom d'entreprise (si professionnel) */}
                            {formData.typeCompte === 'professionnel' && (
                                <div className="auth-field">
                                    <label htmlFor="nomEntreprise" className="auth-label">
                                        Nom de l'entreprise
                                    </label>
                                    <input
                                        id="nomEntreprise"
                                        name="nomEntreprise"
                                        type="text"
                                        value={formData.nomEntreprise}
                                        onChange={handleChange}
                                        className="auth-input"
                                        placeholder="Nom de votre entreprise"
                                    />
                                </div>
                            )}

                            {/* Mot de passe */}
                            <div className="auth-field">
                                <label htmlFor="motDePasse" className="auth-label">
                                    Mot de passe
                                </label>
                                <input
                                    id="motDePasse"
                                    name="motDePasse"
                                    type="password"
                                    required
                                    value={formData.motDePasse}
                                    onChange={handleChange}
                                    className="auth-input"
                                    placeholder="Au moins 6 caractères"
                                />
                            </div>

                            {/* Confirmation mot de passe */}
                            <div className="auth-field">
                                <label htmlFor="confirmMotDePasse" className="auth-label">
                                    Confirmer le mot de passe
                                </label>
                                <input
                                    id="confirmMotDePasse"
                                    name="confirmMotDePasse"
                                    type="password"
                                    required
                                    value={formData.confirmMotDePasse}
                                    onChange={handleChange}
                                    className="auth-input"
                                    placeholder="Confirmez votre mot de passe"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className={`auth-button ${loading ? 'loading' : ''}`}
                        >
                            {loading ? 'Inscription...' : 'Créer mon compte'}
                        </button>

                        <div className="auth-footer">
                            <span className="auth-footer-text">
                                Vous avez déjà un compte ?{' '}
                                <Link to="/login">
                                    Connectez-vous
                                </Link>
                            </span>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RegisterPage;
